import { createEmptyPublicProspectForm } from './publicOwnerResponseForm.js';
import { createEmptyValuationDraft } from './publicProspectValuation.js';

/** A token change always starts with a fresh, non-sensitive page model. */
export function createPublicResponsePageState(token, generation) {
  return {
    token,
    generation,
    property: null,
    receivedIntents: [],
    form: createEmptyPublicProspectForm(),
    valuation: createEmptyValuationDraft(),
    valuationStep: 1,
    errors: {},
    message: '',
    attempt: null,
    attemptDirty: false,
    status: 'loading',
    step: 'hero',
    selectedIntent: null,
    lastSubmittedIntent: null,
    contactOpen: false,
  };
}

export function createReceivedPublicResponseState(property, receivedIntents = [], lastSubmittedIntent = null) {
  return {
    property,
    receivedIntents: Array.isArray(receivedIntents) ? [...receivedIntents] : [],
    form: createEmptyPublicProspectForm(),
    valuation: createEmptyValuationDraft(),
    valuationStep: 1,
    errors: {},
    message: '',
    attempt: null,
    attemptDirty: false,
    status: 'ready',
    step: 'confirmation',
    selectedIntent: null,
    lastSubmittedIntent,
    contactOpen: false,
  };
}

/**
 * Narrowly scoped sensitive-draft wipe for terminal access outcomes.
 * Does not decide status — callers apply this only after the outcome is terminal.
 */
export function createClearedSensitivePublicDrafts(language = 'fr') {
  return {
    form: createEmptyPublicProspectForm(language),
    valuation: createEmptyValuationDraft(),
    valuationStep: 1,
    errors: {},
    message: '',
    attempt: null,
    attemptDirty: false,
    selectedIntent: null,
  };
}

export function isTerminalPublicAccessStatus(status) {
  return status === 'expired' || status === 'unavailable';
}

export function ownsPublicResponseGeneration(owner, active) {
  return Boolean(owner && active && owner.token === active.token && owner.generation === active.generation);
}

export function ownsLatestResolver(owner, active, latestRequestId) {
  return ownsPublicResponseGeneration(owner, active) && owner.requestId === latestRequestId;
}

export function releaseOwnedInFlight(currentOwner, completingOwner) {
  return currentOwner === completingOwner ? null : currentOwner;
}

/** Used by tests and UI to prove language-only edits do not reset entered values. */
export function withPublicResponseLanguage(form, language) {
  return { ...form, language };
}

export function isIntentReceived(receivedIntents, intent) {
  return Array.isArray(receivedIntents) && receivedIntents.includes(intent);
}

/**
 * Explicit decision for a dirty-attempt access preflight.
 * Callers must not create an operation ID or invoke submission unless decision is "continue".
 *
 * @returns {'continue' | 'already_received' | 'abort'}
 */
export function decideDirtyAttemptPreflight(resolved, intent) {
  if (resolved == null || typeof resolved !== 'object' || Array.isArray(resolved)) return 'abort';
  if (resolved.terminalStatus || resolved.resolveFailed) return 'abort';
  if (!resolved.property || typeof resolved.property !== 'object' || Array.isArray(resolved.property)) {
    return 'abort';
  }
  if (!Array.isArray(resolved.receivedIntents)) return 'abort';
  if (isIntentReceived(resolved.receivedIntents, intent)) return 'already_received';
  return 'continue';
}

/**
 * Behavioral gate for dirty-attempt preflight — used by UI and tests.
 * A failed/stale/terminal preflight never permits a new operation or callable invoke.
 */
export function evaluateDirtyAttemptPreflightGate({ resolved, intent }) {
  const decision = decideDirtyAttemptPreflight(resolved, intent);
  return {
    decision,
    mayCreateOperation: decision === 'continue',
    mayInvokeSubmit: decision === 'continue',
    shouldFinishSuccess: decision === 'already_received',
    shouldClearAttemptDirty: decision === 'continue',
  };
}

let nextDirtyPreflightId = 0;

/** Create a unique synchronous preflight ownership token for one dirty-attempt resolve. */
export function createDirtyPreflightOwner({ token, generation, intent }) {
  return {
    kind: 'dirty_preflight',
    id: ++nextDirtyPreflightId,
    token,
    generation,
    intent,
  };
}

export function tryAcquireDirtyPreflightLock(lockRef, owner, inFlightRef = null) {
  if (!lockRef || !owner) return false;
  if (lockRef.current) return false;
  if (inFlightRef?.current) return false;
  lockRef.current = owner;
  return true;
}

export function ownsDirtyPreflightLock(lockRef, owner) {
  return Boolean(lockRef && owner && lockRef.current === owner);
}

export function releaseDirtyPreflightLock(lockRef, owner) {
  if (!lockRef || !owner) return false;
  if (lockRef.current !== owner) return false;
  lockRef.current = null;
  return true;
}

/** Form/header/actions are locked during submit or dirty preflight network wait. */
export function shouldDisablePublicProspectControls({ status, preflightPending }) {
  return status === 'submitting' || preflightPending === true;
}

/**
 * After an async dirty preflight completes, decide whether this owner may still act.
 * Stale/unowned completions must not submit or overwrite terminal UI.
 */
export function evaluateDirtyPreflightCompletionOwnership({
  owner,
  generationOwner,
  lockRef,
  mounted = true,
}) {
  if (!mounted) return { mayAct: false, reason: 'unmounted' };
  if (!ownsPublicResponseGeneration(owner, generationOwner)) {
    return { mayAct: false, reason: 'stale_generation' };
  }
  if (!ownsDirtyPreflightLock(lockRef, owner)) {
    return { mayAct: false, reason: 'unowned_lock' };
  }
  return { mayAct: true, reason: null };
}

/**
 * Release a completed preflight without replacing an expired/unavailable status.
 * Returns the next preflightPending flag (always false when released by this owner).
 */
export function releaseDirtyPreflightUiState({
  lockRef,
  owner,
  status,
}) {
  const released = releaseDirtyPreflightLock(lockRef, owner);
  return {
    released,
    preflightPending: released ? false : Boolean(lockRef?.current),
    preserveTerminalStatus: isTerminalPublicAccessStatus(status),
  };
}

/**
 * After a successful dirty preflight, transfer ownership to the in-flight submission
 * atomically (same synchronous turn) so a second submit cannot slip between locks.
 */
export function transferDirtyPreflightToInFlight({
  lockRef,
  inFlightRef,
  preflightOwner,
  submissionOwner,
}) {
  if (!lockRef || !inFlightRef || !preflightOwner || !submissionOwner) {
    return { transferred: false, preflightPending: Boolean(lockRef?.current) };
  }
  if (!ownsDirtyPreflightLock(lockRef, preflightOwner)) {
    return { transferred: false, preflightPending: Boolean(lockRef.current) };
  }
  if (inFlightRef.current) {
    return { transferred: false, preflightPending: true };
  }
  inFlightRef.current = submissionOwner;
  releaseDirtyPreflightLock(lockRef, preflightOwner);
  return { transferred: true, preflightPending: false };
}

/**
 * Post-resolve decision tree for one owned dirty-preflight attempt.
 * Does not perform network I/O — callers acquire the lock before await resolve().
 */
export function completeOwnedDirtyPreflight({
  owner,
  generationOwner,
  lockRef,
  mounted = true,
  resolved,
  intent,
  status = 'ready',
}) {
  const ownership = evaluateDirtyPreflightCompletionOwnership({
    owner,
    generationOwner,
    lockRef,
    mounted,
  });
  if (!ownership.mayAct) {
    const release = releaseDirtyPreflightUiState({ lockRef, owner, status });
    return {
      outcome: 'stale',
      reason: ownership.reason,
      mayCreateOperation: false,
      mayInvokeSubmit: false,
      shouldFinishSuccess: false,
      released: release.released,
      preflightPending: release.released ? false : Boolean(lockRef?.current),
      preserveTerminalStatus: release.preserveTerminalStatus,
      preflightOwner: null,
    };
  }

  const gate = evaluateDirtyAttemptPreflightGate({ resolved, intent });
  if (gate.shouldFinishSuccess) {
    releaseDirtyPreflightLock(lockRef, owner);
    return {
      outcome: 'already_received',
      mayCreateOperation: false,
      mayInvokeSubmit: false,
      shouldFinishSuccess: true,
      released: true,
      preflightPending: false,
      preserveTerminalStatus: isTerminalPublicAccessStatus(status),
      preflightOwner: null,
      resolved,
    };
  }

  if (!gate.mayCreateOperation) {
    const release = releaseDirtyPreflightUiState({ lockRef, owner, status });
    return {
      outcome: 'abort',
      mayCreateOperation: false,
      mayInvokeSubmit: false,
      shouldFinishSuccess: false,
      released: release.released,
      preflightPending: false,
      preserveTerminalStatus: release.preserveTerminalStatus,
      preflightOwner: null,
    };
  }

  // Keep the lock until sendAttempt transfers it to inFlight.
  return {
    outcome: 'continue',
    mayCreateOperation: true,
    mayInvokeSubmit: true,
    shouldFinishSuccess: false,
    shouldClearAttemptDirty: true,
    released: false,
    preflightPending: true,
    preserveTerminalStatus: false,
    preflightOwner: owner,
  };
}
